import { useSubscription } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { reconnectWebsocket } from '@shared/api/client';
import { Queries } from '@shared/api/queries';
import { Subscriptions } from '@shared/api/subscriptions';
import { API } from '@shared/types/api';
import { useChannelsFromCache } from '@shared/util/hooks';
import { useCardPaginationActions } from './contexts';

function useReconnectWebsocketWhenChannelIsAddedOrRemoved() {
  const channels = useChannelsFromCache();
  const [previousChannelListKey, setPreviousChannelListKey] = useState('');
  const channelListKey = useMemo(
    () =>
      channels
        ?.map(c => c.id)
        .sort()
        .join(','),
    [channels],
  );

  useEffect(() => {
    if (!isEmpty(previousChannelListKey) && previousChannelListKey !== channelListKey) {
      reconnectWebsocket();
    }
    setPreviousChannelListKey(channelListKey);
  }, [channelListKey, previousChannelListKey]);
}

function useCardPaginationSubscription() {
  const { addCard, updateCard, removeCard } = useCardPaginationActions();

  useSubscription<API.CardEventSubscription>(Subscriptions.cards.event, {
    onData(options) {
      if (isNil(options.data.data)) {
        return;
      }
      const { type, cards } = options.data.data.event;
      switch (type) {
        case 'CARD_CREATED':
          cards.forEach(card => addCard(card, true));
          break;
        case 'CARD_UPDATED':
          cards.forEach(card => updateCard(card, true));
          break;
        case 'CARD_DELETED':
          cards.forEach(({ id }) => removeCard(id, true));
          cards.forEach(({ id }) =>
            options.client.writeQuery({
              data: { card: null },
              query: Queries.card.details,
              variables: { id },
            }),
          );
          break;
      }
    },
  });
}

export function CardPaginationSubscriptions() {
  useCardPaginationSubscription();
  useReconnectWebsocketWhenChannelIsAddedOrRemoved();
  return null;
}
